import React, { useMemo, useState } from 'react';
import Layout from '../../../components/Layout';

/* ============================================================================
 * MOCKUP — Editing riga preventivo SEMPLICE
 * Caso: TIPO RIGA = "Articolo configurato" + MODALITÀ = "Parto da un pre-configurato"
 *
 * Tutti i dati sono hardcoded. Nessuna chiamata API.
 * Riflette le decisioni di design concordate con Paolo:
 *  - Autocomplete preconfigurato (no combo nativa)
 *  - Creazione riga implicita alla selezione, cleanup su Annulla
 *  - Blocco 4 visibile ed editabile, precompilato dal preconfigurato
 *  - Tabella componenti con tutte le colonne economiche sempre visibili,
 *    raggruppate in "Strutturali / Quantità & Sfrido / Economia"
 *  - Intercambiabili gestiti come nel blocco 4 (hint box prezzo max)
 *  - Cambio preconfigurato: confirm distruttiva
 * ============================================================================ */

// ── Icone ───────────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Tipi locali ─────────────────────────────────────────────────────────────

type TipologiaComponente = 'ARTICOLO_FISICO' | 'INTERCAMBIABILE' | 'LAVORAZIONE_PRESTAZIONE';
type ApproccioCalcolo = 'NESSUNO' | 'RICARICO_SU_COSTI' | 'SCONTO_DA_LISTINO';

interface PreconfOption {
  id: number;
  codice: string;
  nome: string;
  articoloPadreCodice: string;
  articoloPadreDescrizione: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

interface ComponenteMock {
  id: number;
  ordine: number;
  tipologia: TipologiaComponente;
  categoriaNome: string;
  articoloId: number | null;
  articoloCodice: string;
  articoloDescrizione: string;
  haFormato: boolean;
  /** solo per INTERCAMBIABILE */
  numeroAlternativi?: number;
  prezzoMaxAlternativi?: number;
  umVendita: string;
  quantita: number | null;
  sfrido: number | null;
  prezzoListino: number | null;
  scontoAcquisto: number | null;
  scontoVendita: number | null;
}

// ── Dati hardcoded ──────────────────────────────────────────────────────────

const PRECONFIGURATI_MOCK: PreconfOption[] = [
  {
    id: 1,
    codice: 'IMPORTATO_2-PRECONF1',
    nome: 'Pavimento posato in opera con colla — Gres 60×60',
    articoloPadreCodice: 'PAV-GRES-60',
    articoloPadreDescrizione: 'Pavimentazione gres porcellanato 60×60 (posato)',
    umVenditaSimbolo: 'mq',
    nComponenti: 3,
  },
  {
    id: 2,
    codice: 'BAGNO-COMPLETO-1',
    nome: 'Bagno completo chiavi in mano — Classic',
    articoloPadreCodice: 'BAGNO-CLS',
    articoloPadreDescrizione: 'Bagno completo 4 m² configurazione classica',
    umVenditaSimbolo: 'cad',
    nComponenti: 8,
  },
  {
    id: 3,
    codice: 'RIVEST-MURO-30X60',
    nome: 'Rivestimento murale ceramica 30×60',
    articoloPadreCodice: 'RIV-CER-3060',
    articoloPadreDescrizione: 'Rivestimento ceramico 30×60 posato',
    umVenditaSimbolo: 'mq',
    nComponenti: 2,
  },
  {
    id: 4,
    codice: 'PARQUET-POSA',
    nome: 'Parquet listoni laminato con posa',
    articoloPadreCodice: 'PRK-LAM',
    articoloPadreDescrizione: 'Parquet laminato AC4 con posa flottante',
    umVenditaSimbolo: 'mq',
    nComponenti: 4,
  },
];

const COMPONENTI_MOCK_PRECONF_1: ComponenteMock[] = [
  {
    id: 101,
    ordine: 1,
    tipologia: 'ARTICOLO_FISICO',
    categoriaNome: 'Pavimenti',
    articoloId: 2001,
    articoloCodice: 'PIAS-001-60X60',
    articoloDescrizione: 'Piastrella Gres 60×60',
    haFormato: true,
    umVendita: 'mq',
    quantita: 1,
    sfrido: 5,
    prezzoListino: 28.50,
    scontoAcquisto: 55,
    scontoVendita: 20,
  },
  {
    id: 102,
    ordine: 2,
    tipologia: 'INTERCAMBIABILE',
    categoriaNome: 'Colla',
    articoloId: 2002,
    articoloCodice: 'C2TE00001',
    articoloDescrizione: 'COLLA C2TE NON ALLEGGERITA',
    haFormato: false,
    numeroAlternativi: 3,
    prezzoMaxAlternativi: 1.80,
    umVendita: 'kg',
    quantita: 4,
    sfrido: null,
    prezzoListino: 1.80,
    scontoAcquisto: 30,
    scontoVendita: 0,
  },
  {
    id: 103,
    ordine: 3,
    tipologia: 'LAVORAZIONE_PRESTAZIONE',
    categoriaNome: 'Pose in opera',
    articoloId: 2003,
    articoloCodice: 'IMPORTATO_3',
    articoloDescrizione: 'Posa piastrelle Gres 60×60 (prestazione)',
    haFormato: false,
    umVendita: 'mq',
    quantita: 1,
    sfrido: null,
    prezzoListino: 22.00,
    scontoAcquisto: 0,
    scontoVendita: 0,
  },
];

// ── Utility formattazione numeri (replica utils/formatters) ─────────────────

function formatNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatCurrency(n: number | null | undefined): string {
  return formatNumber(n);
}
function formatQuantity(n: number | null | undefined): string {
  return formatNumber(n);
}

// ── Calcoli economici (replica logica backend) ──────────────────────────────

function costoComponente(c: ComponenteMock): number | null {
  if (c.prezzoListino == null) return null;
  const sa = c.scontoAcquisto ?? 0;
  return c.prezzoListino * (1 - sa / 100);
}
function prezzoVenditaComponente(c: ComponenteMock): number | null {
  if (c.prezzoListino == null) return null;
  const sv = c.scontoVendita ?? 0;
  return c.prezzoListino * (1 - sv / 100);
}
function qtaEffettiva(c: ComponenteMock): number {
  const q = c.quantita ?? 0;
  const sf = c.sfrido ?? 0;
  return q * (1 + sf / 100);
}

// ── Componente principale ───────────────────────────────────────────────────

export default function MockupRigaPreventivoPreconfigurato() {
  // Stato di alto livello
  const [preconfSelezionato, setPreconfSelezionato] = useState<PreconfOption | null>(null);
  const [rigaCreataInDb, setRigaCreataInDb] = useState<boolean>(false);
  const [showConfirmCambio, setShowConfirmCambio] = useState(false);
  const [preconfDaCambiare, setPreconfDaCambiare] = useState<PreconfOption | null>(null);

  // Blocco 4 — editabile, precompilato
  const [descrizione, setDescrizione] = useState('');
  const [descrizioneEstesa, setDescrizioneEstesa] = useState('');
  const [umVendita, setUmVendita] = useState('');
  const [quantita, setQuantita] = useState<string>('1');
  const [scontoRiga, setScontoRiga] = useState<string>('0');
  const [codiceIva, setCodiceIva] = useState<string>('22');

  // Blocco 5 — componenti
  const [componenti, setComponenti] = useState<ComponenteMock[]>([]);

  // Blocco 6 — approccio prezzo
  const [approccio, setApproccio] = useState<ApproccioCalcolo>('SCONTO_DA_LISTINO');
  const [ricaricoPerc, setRicaricoPerc] = useState<string>('30');
  const [prezzoOverride, setPrezzoOverride] = useState(false);
  const [prezzoUnitarioManuale, setPrezzoUnitarioManuale] = useState<string>('');

  // ── Selezione preconfigurato ──────────────────────────────────────────────
  const selezionaPreconfigurato = (p: PreconfOption) => {
    // Simula POST combinato: crea riga + inizializza componenti + precompila blocco 4
    setPreconfSelezionato(p);
    setRigaCreataInDb(true);
    setDescrizione(p.nome);
    setDescrizioneEstesa('');
    setUmVendita(p.umVenditaSimbolo);
    setQuantita('1');
    setScontoRiga('0');
    // Componenti copiati dal preconfigurato (solo il preconf id=1 ha componenti mock)
    if (p.id === 1) {
      setComponenti(JSON.parse(JSON.stringify(COMPONENTI_MOCK_PRECONF_1)));
    } else {
      // Placeholder: altri preconfigurati generano componenti vuoti per il mockup
      setComponenti([]);
    }
  };

  const richiediCambioPreconfigurato = (p: PreconfOption) => {
    // Se l'utente ha modificato qualcosa nei componenti, confirm distruttiva
    setPreconfDaCambiare(p);
    setShowConfirmCambio(true);
  };

  const confermaCambio = () => {
    if (preconfDaCambiare) {
      selezionaPreconfigurato(preconfDaCambiare);
    }
    setShowConfirmCambio(false);
    setPreconfDaCambiare(null);
  };

  const rimuoviPreconfigurato = () => {
    // Simula cleanup su Annulla: DELETE della riga fantasma
    setPreconfSelezionato(null);
    setRigaCreataInDb(false);
    setDescrizione('');
    setDescrizioneEstesa('');
    setUmVendita('');
    setComponenti([]);
  };

  const reimpostaComponentiDaPreconfigurato = () => {
    if (!preconfSelezionato) return;
    if (!window.confirm('Reimpostare i componenti dal preconfigurato? Le modifiche ai componenti saranno perse.')) return;
    if (preconfSelezionato.id === 1) {
      setComponenti(JSON.parse(JSON.stringify(COMPONENTI_MOCK_PRECONF_1)));
    }
  };

  // ── Aggiornamento componente ──────────────────────────────────────────────
  const aggiornaComp = (id: number, patch: Partial<ComponenteMock>) => {
    setComponenti(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const eliminaComp = (id: number) => {
    if (!window.confirm('Eliminare il componente?')) return;
    setComponenti(prev => prev.filter(c => c.id !== id));
  };

  // ── Prezzo calcolato della riga ───────────────────────────────────────────
  const prezzoCalcolatoRiga = useMemo<number | null>(() => {
    if (componenti.length === 0) return null;
    if (approccio === 'SCONTO_DA_LISTINO') {
      // somma prezzi vendita componenti × qta effettiva (con sfrido)
      return componenti.reduce((acc, c) => {
        const pv = prezzoVenditaComponente(c);
        if (pv == null) return acc;
        return acc + pv * qtaEffettiva(c);
      }, 0);
    }
    if (approccio === 'RICARICO_SU_COSTI') {
      const costoTotale = componenti.reduce((acc, c) => {
        const co = costoComponente(c);
        if (co == null) return acc;
        return acc + co * qtaEffettiva(c);
      }, 0);
      const r = parseFloat(ricaricoPerc || '0');
      return costoTotale * (1 + r / 100);
    }
    return null;
  }, [componenti, approccio, ricaricoPerc]);

  const prezzoUnitarioFinale = prezzoOverride
    ? parseFloat(prezzoUnitarioManuale || '0') || 0
    : (prezzoCalcolatoRiga ?? 0);

  const importoNetto = useMemo(() => {
    const q = parseFloat(quantita || '0') || 0;
    const s = parseFloat(scontoRiga || '0') || 0;
    return q * prezzoUnitarioFinale * (1 - s / 100);
  }, [quantita, scontoRiga, prezzoUnitarioFinale]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <style>{INLINE_STYLES}</style>

      <div className="bop-page">
        <div className="bop-page__header">
          <div>
            <h1 className="bop-page__title">Mockup — Riga preventivo con preconfigurato</h1>
            <p className="bop-page__subtitle">
              <span className="bop-badge bop-badge--warning">MOCKUP</span>{' '}
              Tutti i dati sono hardcoded. Nessuna chiamata API. Usato per validare UI/UX prima del refactor della modal reale.
            </p>
          </div>
        </div>

        {/* Simulazione finestra modal */}
        <div className="mrp-modal-container">
          <div className="mrp-modal-header">
            <h2 className="mrp-modal-title">Aggiungi riga</h2>
            <button className="bop-btn-icon" title="Chiudi (simulato)"><XIcon /></button>
          </div>

          <div className="mrp-modal-body">

            {/* ── BLOCCO 1 — Tipo riga ──────────────────────────────────── */}
            <div className="mrp-section">
              <div className="mrp-section__title">1. Tipo riga</div>
              <div className="mrp-radio-row">
                <label className="mrp-radio-tile">
                  <input type="radio" name="tipo" />
                  <div>
                    <div className="mrp-radio-tile__title">Articolo</div>
                    <div className="mrp-radio-tile__help">Riga standard con un singolo articolo</div>
                  </div>
                </label>
                <label className="mrp-radio-tile mrp-radio-tile--selected">
                  <input type="radio" name="tipo" defaultChecked />
                  <div>
                    <div className="mrp-radio-tile__title">Articolo configurato</div>
                    <div className="mrp-radio-tile__help">Riga con componenti e calcolo prezzo</div>
                  </div>
                </label>
                <label className="mrp-radio-tile">
                  <input type="radio" name="tipo" />
                  <div>
                    <div className="mrp-radio-tile__title">Descrittiva</div>
                    <div className="mrp-radio-tile__help">Solo testo, nessun prezzo</div>
                  </div>
                </label>
              </div>
            </div>

            {/* ── BLOCCO 2 — Modalità di configurazione ─────────────────── */}
            <div className="mrp-section">
              <div className="mrp-section__title">2. Modalità di configurazione</div>
              <div className="mrp-radio-row">
                <label className="mrp-radio-tile">
                  <input type="radio" name="modalita" />
                  <div>
                    <div className="mrp-radio-tile__title">Parto da un modello</div>
                    <div className="mrp-radio-tile__help">Es. modello mobile da bagno</div>
                  </div>
                </label>
                <label className="mrp-radio-tile mrp-radio-tile--selected">
                  <input type="radio" name="modalita" defaultChecked />
                  <div>
                    <div className="mrp-radio-tile__title">Parto da un pre-configurato</div>
                    <div className="mrp-radio-tile__help">Es. pavimento posato, bagno completo</div>
                  </div>
                </label>
                <label className="mrp-radio-tile">
                  <input type="radio" name="modalita" />
                  <div>
                    <div className="mrp-radio-tile__title">Configurazione differita</div>
                    <div className="mrp-radio-tile__help">Completata in ordine (prezzo manuale)</div>
                  </div>
                </label>
              </div>

              <div className="mrp-field-block">
                <label className="bop-field__label">Articolo pre-configurato</label>
                {preconfSelezionato ? (
                  <PreconfiguratoSelectedBadge
                    preconf={preconfSelezionato}
                    onCambia={richiediCambioPreconfigurato}
                    onRimuovi={rimuoviPreconfigurato}
                    onReimposta={reimpostaComponentiDaPreconfigurato}
                  />
                ) : (
                  <AutocompletePreconfigurato onSeleziona={selezionaPreconfigurato} />
                )}

                {rigaCreataInDb && preconfSelezionato && (
                  <div className="mrp-info-chip">
                    <InfoIcon />
                    <span>Riga creata automaticamente in bozza. Modifica i campi sotto e premi <strong>Salva</strong> per confermare, oppure <strong>Annulla</strong> per scartarla.</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── BLOCCO 4 — Articolo / Descrizione (editabile, precompilato) ── */}
            {preconfSelezionato && (
              <div className="mrp-section">
                <div className="mrp-section__title">4. Articolo / Descrizione</div>

                <div className="mrp-articolo-readout">
                  <div className="mrp-articolo-readout__label">Articolo padre (dal preconfigurato)</div>
                  <div className="mrp-articolo-readout__body">
                    <span className="bop-badge bop-badge--info">{preconfSelezionato.articoloPadreCodice}</span>
                    <span className="mrp-articolo-readout__desc">{preconfSelezionato.articoloPadreDescrizione}</span>
                  </div>
                  <small className="bop-text-muted">
                    L'articolo viene dal preconfigurato selezionato. Per cambiarlo, cambia preconfigurato.
                  </small>
                </div>

                <div className="bop-form-group">
                  <label className="bop-field__label">Descrizione *</label>
                  <input
                    className="bop-field__input"
                    value={descrizione}
                    onChange={e => setDescrizione(e.target.value)}
                  />
                  <small className="bop-text-muted">Precompilata con il nome del preconfigurato. Editabile.</small>
                </div>

                <div className="bop-form-group">
                  <label className="bop-field__label">Descrizione estesa</label>
                  <textarea
                    className="bop-field__textarea"
                    rows={2}
                    value={descrizioneEstesa}
                    onChange={e => setDescrizioneEstesa(e.target.value)}
                    placeholder="Note aggiuntive, specifiche tecniche…"
                  />
                </div>

                <div className="mrp-grid-4">
                  <div className="bop-form-group">
                    <label className="bop-field__label">U.M.</label>
                    <input className="bop-field__input" value={umVendita} readOnly disabled title="U.M. dal padre del preconfigurato" />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-field__label">Quantità</label>
                    <input
                      className="bop-field__input"
                      type="number"
                      step="0.01"
                      value={quantita}
                      onChange={e => setQuantita(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-field__label">Sconto riga %</label>
                    <input
                      className="bop-field__input"
                      type="number"
                      step="0.01"
                      value={scontoRiga}
                      onChange={e => setScontoRiga(e.target.value)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-field__label">Codice IVA</label>
                    <select className="bop-field__select" value={codiceIva} onChange={e => setCodiceIva(e.target.value)}>
                      <option value="22">22 — IVA ordinaria (22%)</option>
                      <option value="10">10 — IVA ridotta (10%)</option>
                      <option value="4">04 — IVA agevolata (4%)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ── BLOCCO 5 — Componenti (raggruppato) ──────────────────── */}
            {preconfSelezionato && (
              <div className="mrp-section">
                <div className="mrp-section__title">
                  5. Componenti
                  <span className="bop-badge bop-badge--primary mrp-badge-inline">{componenti.length}</span>
                </div>

                {componenti.length === 0 ? (
                  <div className="mrp-empty">
                    Nessun componente. Premi <em>Reimposta da preconfigurato</em> o aggiungine uno.
                  </div>
                ) : (
                  <div className="mrp-table-wrap">
                    <table className="bop-table mrp-comp-table">
                      <thead>
                        <tr className="mrp-group-header-row">
                          <th colSpan={4} className="mrp-group-header mrp-group-header--struct">Strutturali</th>
                          <th colSpan={2} className="mrp-group-header mrp-group-header--qta">Quantità &amp; Sfrido</th>
                          <th colSpan={5} className="mrp-group-header mrp-group-header--econ">Economia</th>
                          <th className="mrp-group-header mrp-group-header--actions"></th>
                        </tr>
                        <tr>
                          <th style={{ width: 40 }}>#</th>
                          <th style={{ width: 130 }}>Tipologia</th>
                          <th style={{ width: 120 }}>Categoria</th>
                          <th>Articolo scelto</th>

                          <th style={{ width: 110 }}>Qtà / UM</th>
                          <th style={{ width: 90 }}>Sfrido %</th>

                          <th style={{ width: 110 }}>P. listino €</th>
                          <th style={{ width: 85 }}>Sc. acq. %</th>
                          <th style={{ width: 85 }}>Sc. ven. %</th>
                          <th style={{ width: 100 }} className="bop-text-right">Costo un.</th>
                          <th style={{ width: 110 }} className="bop-text-right">P. vendita un.</th>

                          <th style={{ width: 50 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {componenti.map(c => {
                          const costo = costoComponente(c);
                          const pv = prezzoVenditaComponente(c);
                          return (
                            <tr key={c.id}>
                              <td className="bop-text-muted">{c.ordine}</td>
                              <td><TipologiaBadge t={c.tipologia} /></td>
                              <td>{c.categoriaNome}</td>
                              <td>
                                <ArticoloCell comp={c} onCambia={() => alert('Modal scelta articolo (non implementata in mockup)')} onRimuovi={() => aggiornaComp(c.id, { articoloId: null, articoloCodice: '', articoloDescrizione: '' })} />
                                {c.tipologia === 'INTERCAMBIABILE' && c.numeroAlternativi != null && c.prezzoMaxAlternativi != null && (
                                  <div className="mrp-inter-hint">
                                    <InfoIcon />
                                    <span>
                                      Intercambiabile · {c.numeroAlternativi} alternativi ·
                                      prezzo max <strong>€ {formatCurrency(c.prezzoMaxAlternativi)}</strong> usato come listino
                                    </span>
                                  </div>
                                )}
                              </td>

                              <td>
                                <div className="mrp-qty-cell">
                                  <input
                                    className="bop-field__input mrp-inp-xs"
                                    type="number"
                                    step="0.001"
                                    value={c.quantita ?? ''}
                                    onChange={e => aggiornaComp(c.id, { quantita: e.target.value === '' ? null : Number(e.target.value) })}
                                  />
                                  <span className="bop-text-muted">{c.umVendita}</span>
                                </div>
                              </td>
                              <td>
                                {c.tipologia === 'ARTICOLO_FISICO' && c.haFormato ? (
                                  <input
                                    className="bop-field__input mrp-inp-xs"
                                    type="number"
                                    step="1"
                                    value={c.sfrido ?? ''}
                                    onChange={e => aggiornaComp(c.id, { sfrido: e.target.value === '' ? null : Number(e.target.value) })}
                                  />
                                ) : (
                                  <span className="bop-text-muted">—</span>
                                )}
                              </td>

                              <td>
                                <input
                                  className="bop-field__input mrp-inp-sm"
                                  type="number"
                                  step="0.01"
                                  value={c.prezzoListino ?? ''}
                                  onChange={e => aggiornaComp(c.id, { prezzoListino: e.target.value === '' ? null : Number(e.target.value) })}
                                />
                              </td>
                              <td>
                                <input
                                  className="bop-field__input mrp-inp-xs"
                                  type="number"
                                  step="0.01"
                                  value={c.scontoAcquisto ?? ''}
                                  onChange={e => aggiornaComp(c.id, { scontoAcquisto: e.target.value === '' ? null : Number(e.target.value) })}
                                />
                              </td>
                              <td>
                                <input
                                  className="bop-field__input mrp-inp-xs"
                                  type="number"
                                  step="0.01"
                                  value={c.scontoVendita ?? ''}
                                  onChange={e => aggiornaComp(c.id, { scontoVendita: e.target.value === '' ? null : Number(e.target.value) })}
                                />
                              </td>
                              <td className="bop-text-right mrp-calc-cell">
                                € {formatCurrency(costo)}
                              </td>
                              <td className="bop-text-right mrp-calc-cell">
                                € {formatCurrency(pv)}
                              </td>

                              <td>
                                <button className="bop-btn-icon" title="Elimina" onClick={() => eliminaComp(c.id)}>
                                  <TrashIcon />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mrp-comp-actions">
                  <button className="bop-btn bop-btn-secondary" onClick={() => alert('Aggiungi componente libero (da implementare)')}>
                    + Aggiungi componente libero
                  </button>
                  <button className="bop-btn bop-btn-ghost" onClick={reimpostaComponentiDaPreconfigurato}>
                    <RefreshIcon /> Reimposta da preconfigurato
                  </button>
                </div>
              </div>
            )}

            {/* ── BLOCCO 6 — Calcolo prezzo ─────────────────────────────── */}
            {preconfSelezionato && (
              <div className="mrp-section">
                <div className="mrp-section__title">6. Calcolo prezzo</div>

                <div className="mrp-prezzi-grid">
                  {/* Calcolo da componenti */}
                  <div className="mrp-prezzi-panel">
                    <div className="mrp-prezzi-panel__title">Calcolo da componenti</div>

                    <div className="bop-form-group">
                      <label className="bop-field__label">Approccio</label>
                      <select
                        className="bop-field__select"
                        value={approccio}
                        onChange={e => setApproccio(e.target.value as ApproccioCalcolo)}
                      >
                        <option value="SCONTO_DA_LISTINO">Somma prezzi vendita (sconto da listino)</option>
                        <option value="RICARICO_SU_COSTI">Costi + ricarico %</option>
                        <option value="NESSUNO">Prezzo manuale</option>
                      </select>
                    </div>

                    {approccio === 'RICARICO_SU_COSTI' && (
                      <div className="bop-form-group">
                        <label className="bop-field__label">Ricarico riga %</label>
                        <input
                          className="bop-field__input mrp-inp-sm"
                          type="number"
                          step="0.01"
                          value={ricaricoPerc}
                          onChange={e => setRicaricoPerc(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="mrp-prezzo-calc-box">
                      <div className="mrp-prezzo-calc-box__label">Prezzo calcolato</div>
                      <div className="mrp-prezzo-calc-box__value">
                        € {formatCurrency(prezzoCalcolatoRiga)} <span className="bop-text-muted">/ {umVendita}</span>
                      </div>
                    </div>
                  </div>

                  {/* Prezzo finale */}
                  <div className="mrp-prezzi-panel mrp-prezzi-panel--highlight">
                    <div className="mrp-prezzi-panel__title">Prezzo finale riga</div>

                    <label className="mrp-check-label">
                      <input
                        type="checkbox"
                        checked={prezzoOverride}
                        onChange={e => {
                          setPrezzoOverride(e.target.checked);
                          if (e.target.checked && !prezzoUnitarioManuale && prezzoCalcolatoRiga != null) {
                            setPrezzoUnitarioManuale(String(prezzoCalcolatoRiga.toFixed(2)));
                          }
                        }}
                      />
                      <span>Sovrascrivi prezzo manualmente</span>
                    </label>

                    {prezzoOverride && (
                      <div className="bop-form-group">
                        <label className="bop-field__label">Prezzo unitario manuale € / {umVendita}</label>
                        <input
                          className="bop-field__input mrp-inp-sm"
                          type="number"
                          step="0.01"
                          value={prezzoUnitarioManuale}
                          onChange={e => setPrezzoUnitarioManuale(e.target.value)}
                        />
                      </div>
                    )}

                    <div className="mrp-totali">
                      <div className="mrp-totali__row">
                        <span>Prezzo unitario applicato</span>
                        <strong>€ {formatCurrency(prezzoUnitarioFinale)} / {umVendita}</strong>
                      </div>
                      <div className="mrp-totali__row">
                        <span>Quantità × sconto riga</span>
                        <span>{formatQuantity(parseFloat(quantita || '0'))} {umVendita} − {formatNumber(parseFloat(scontoRiga || '0'))}%</span>
                      </div>
                      <div className="mrp-totali__row mrp-totali__row--total">
                        <span>Importo netto riga</span>
                        <strong>€ {formatCurrency(importoNetto)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mrp-modal-footer">
            <button
              className="bop-btn bop-btn-secondary"
              onClick={rimuoviPreconfigurato}
              disabled={!preconfSelezionato}
              title={preconfSelezionato ? 'Chiude la modal ed elimina la riga in bozza' : undefined}
            >
              Annulla
            </button>
            <button
              className="bop-btn bop-btn-primary"
              disabled={!preconfSelezionato}
              onClick={() => alert('Salva (simulato): PUT /api/preventivi/:id/righe/:rigaId con i dati del blocco 4 e 6')}
            >
              Salva riga
            </button>
          </div>
        </div>

        {/* Confirm cambio preconfigurato */}
        {showConfirmCambio && preconfDaCambiare && (
          <ConfirmCambioPreconfigurato
            nuovo={preconfDaCambiare}
            onConferma={confermaCambio}
            onAnnulla={() => { setShowConfirmCambio(false); setPreconfDaCambiare(null); }}
          />
        )}

        {/* Pannello spiegazione decisioni */}
        <details className="mrp-debug-panel">
          <summary>Decisioni di design applicate in questo mockup</summary>
          <ul>
            <li><strong>Autocomplete</strong> al posto della combo nativa: input con ricerca inline, dropdown a 2 righe (codice + descrizione padre + UM + N° componenti).</li>
            <li><strong>Creazione riga implicita</strong>: alla selezione del preconfigurato parte un POST combinato <code>crea-da-preconfigurato</code> che crea la riga <code>ARTICOLO_CONFIGURATO + DA_PRECONFIGURATO</code> e inizializza i componenti in un'unica transazione. Nel mockup è simulato dallo stato <code>rigaCreataInDb</code>.</li>
            <li><strong>Blocco 4 visibile ed editabile</strong>: articolo padre readonly (dal preconfigurato), descrizione/descrizione estesa/quantità/sconto riga/IVA editabili. Descrizione precompilata con <code>preconfigurato.nome</code>.</li>
            <li><strong>Blocco 5 — tabella completa raggruppata</strong>: header su due righe con gruppi "Strutturali / Quantità & Sfrido / Economia". Nessuna colonna Principale/In-descrizione (metadati del preconfigurato, non modificabili qui).</li>
            <li><strong>Intercambiabili</strong>: il componente INTERCAMBIABILE mostra una info box "N alternativi · prezzo max € X,XX usato come listino" (replicando il pattern del blocco 4 articolo intercambiabile).</li>
            <li><strong>Cambio preconfigurato</strong>: confirm distruttiva → DELETE riga + POST nuovo preconfigurato.</li>
            <li><strong>Annulla</strong>: se la riga è "fantasma" (appena creata automaticamente) → DELETE backend; se si modifica una riga esistente → chiusura senza DELETE (non simulato nel mockup).</li>
            <li><strong>Reimposta da preconfigurato</strong>: ricarica i componenti dal template perdendo eventuali modifiche fatte ai componenti (confirm standard).</li>
            <li><strong>Calcolo prezzo</strong>: 3 approcci (sconto da listino, ricarico su costi, prezzo manuale); "Sovrascrivi" abilita un override manuale; sempre visibili i totali unitario + sconto riga + importo netto.</li>
          </ul>
        </details>

      </div>
    </Layout>
  );
}

// ── Sotto-componenti ────────────────────────────────────────────────────────

function AutocompletePreconfigurato({ onSeleziona }: { onSeleziona: (p: PreconfOption) => void }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const results = query.trim()
    ? PRECONFIGURATI_MOCK.filter(p =>
        p.codice.toLowerCase().includes(query.toLowerCase()) ||
        p.nome.toLowerCase().includes(query.toLowerCase()) ||
        p.articoloPadreDescrizione.toLowerCase().includes(query.toLowerCase())
      )
    : PRECONFIGURATI_MOCK;

  return (
    <div className="mrp-autocomplete">
      <input
        className="bop-field__input"
        placeholder="Cerca per codice o descrizione…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && results.length > 0 && (
        <div className="mrp-autocomplete__dropdown">
          {results.map(p => (
            <button
              key={p.id}
              type="button"
              className="mrp-autocomplete__item"
              onMouseDown={e => { e.preventDefault(); onSeleziona(p); setQuery(''); setOpen(false); }}
            >
              <div className="mrp-autocomplete__line1">
                <span className="mrp-autocomplete__codice">{p.codice}</span>
                <span className="mrp-autocomplete__nome">{p.nome}</span>
              </div>
              <div className="mrp-autocomplete__line2">
                <span>Padre: <strong>{p.articoloPadreCodice}</strong> — {p.articoloPadreDescrizione}</span>
                <span className="mrp-autocomplete__meta">UM {p.umVenditaSimbolo} · {p.nComponenti} componenti</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && (
        <div className="mrp-autocomplete__dropdown">
          <div className="mrp-autocomplete__empty">Nessun preconfigurato trovato</div>
        </div>
      )}
    </div>
  );
}

function PreconfiguratoSelectedBadge({
  preconf, onCambia, onRimuovi, onReimposta,
}: {
  preconf: PreconfOption;
  onCambia: (p: PreconfOption) => void;
  onRimuovi: () => void;
  onReimposta: () => void;
}) {
  return (
    <div className="mrp-preconf-selected">
      <div className="mrp-preconf-selected__main">
        <span className="bop-badge bop-badge--success">{preconf.codice}</span>
        <div className="mrp-preconf-selected__info">
          <div className="mrp-preconf-selected__nome">{preconf.nome}</div>
          <div className="mrp-preconf-selected__meta">
            Padre: <strong>{preconf.articoloPadreCodice}</strong> — {preconf.articoloPadreDescrizione}
            {' · '}UM {preconf.umVenditaSimbolo}
            {' · '}{preconf.nComponenti} componenti
          </div>
        </div>
      </div>
      <div className="mrp-preconf-selected__actions">
        <button
          type="button"
          className="bop-btn bop-btn-ghost"
          onClick={() => {
            // Simula apertura autocomplete per scegliere un altro preconfigurato
            const next = PRECONFIGURATI_MOCK.find(p => p.id !== preconf.id);
            if (next) onCambia(next);
          }}
          title="Cambia preconfigurato (confirm distruttiva)"
        >
          Cambia
        </button>
        <button type="button" className="bop-btn bop-btn-ghost" onClick={onReimposta} title="Reimposta i componenti dal preconfigurato">
          <RefreshIcon /> Reimposta
        </button>
        <button
          type="button"
          className="bop-btn-icon"
          onClick={onRimuovi}
          title="Rimuovi preconfigurato (simula DELETE riga fantasma)"
        >
          <XIcon />
        </button>
      </div>
    </div>
  );
}

function TipologiaBadge({ t }: { t: TipologiaComponente }) {
  const map = {
    ARTICOLO_FISICO: { label: 'ARTICOLO FISICO', cls: 'bop-badge--info' },
    INTERCAMBIABILE: { label: 'INTERCAMBIABILE', cls: 'bop-badge--primary' },
    LAVORAZIONE_PRESTAZIONE: { label: 'LAVORAZIONE', cls: 'bop-badge--warning' },
  } as const;
  return <span className={`bop-badge ${map[t].cls} mrp-tipologia-badge`}>{map[t].label}</span>;
}

function ArticoloCell({
  comp, onCambia, onRimuovi,
}: {
  comp: ComponenteMock;
  onCambia: () => void;
  onRimuovi: () => void;
}) {
  if (!comp.articoloId) {
    return (
      <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={onCambia}>
        <SearchIcon /> Scegli articolo
      </button>
    );
  }
  return (
    <span className="mrp-articolo-chip">
      <strong>{comp.articoloCodice}</strong>
      <span className="mrp-articolo-chip__desc">— {comp.articoloDescrizione}</span>
      <button type="button" className="mrp-articolo-chip__x" onClick={onRimuovi} title="Rimuovi articolo">✕</button>
    </span>
  );
}

function ConfirmCambioPreconfigurato({
  nuovo, onConferma, onAnnulla,
}: {
  nuovo: PreconfOption;
  onConferma: () => void;
  onAnnulla: () => void;
}) {
  return (
    <div className="mrp-confirm-overlay">
      <div className="mrp-confirm-box">
        <div className="mrp-confirm-box__title">Cambiare preconfigurato?</div>
        <p>
          Stai per sostituire il preconfigurato corrente con <strong>{nuovo.codice} — {nuovo.nome}</strong>.<br />
          La riga corrente verrà eliminata e ricreata. <strong>Tutte le modifiche fatte ai componenti andranno perse.</strong>
        </p>
        <div className="mrp-confirm-box__actions">
          <button className="bop-btn bop-btn-secondary" onClick={onAnnulla}>Annulla</button>
          <button className="bop-btn bop-btn-danger" onClick={onConferma}>Sì, cambia preconfigurato</button>
        </div>
      </div>
    </div>
  );
}

// ── Stili locali ─────────────────────────────────────────────────────────────
// Regola BOP: classi bop-* sono sempre nel design system globale.
// Qui uso prefisso 'mrp-' (Mockup Riga Preventivo) per stili locali al mockup.
// Nel refactor reale queste classi andranno promosse in bop-design-system.css
// con prefisso bop-* (es. bop-autocomplete, bop-preconf-selected, ecc.).

const INLINE_STYLES = `
.mrp-modal-container {
  max-width: 1100px;
  margin: var(--bop-spacing-4) auto 0;
  background: var(--bop-color-surface);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-lg);
  box-shadow: var(--bop-shadow-md);
  overflow: hidden;
}
.mrp-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--bop-spacing-3) var(--bop-spacing-4);
  border-bottom: 1px solid var(--bop-color-border);
  background: var(--bop-color-surface-muted);
}
.mrp-modal-title {
  font-size: var(--bop-font-lg);
  font-weight: 600;
  margin: 0;
  color: var(--bop-color-text-primary);
}
.mrp-modal-body {
  padding: var(--bop-spacing-4);
  max-height: calc(100vh - 240px);
  overflow-y: auto;
}
.mrp-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--bop-spacing-2);
  padding: var(--bop-spacing-3) var(--bop-spacing-4);
  border-top: 1px solid var(--bop-color-border);
  background: var(--bop-color-surface-muted);
}

.mrp-section {
  padding: var(--bop-spacing-4);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-md);
  margin-bottom: var(--bop-spacing-4);
  background: var(--bop-color-surface);
}
.mrp-section__title {
  font-size: var(--bop-font-md);
  font-weight: 600;
  margin-bottom: var(--bop-spacing-3);
  color: var(--bop-color-primary-dark);
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: var(--bop-spacing-2);
}

.mrp-radio-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--bop-spacing-2);
  margin-bottom: var(--bop-spacing-3);
}
.mrp-radio-tile {
  display: flex;
  gap: var(--bop-spacing-2);
  padding: var(--bop-spacing-3);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-md);
  cursor: pointer;
  background: var(--bop-color-surface);
  transition: all 0.1s ease;
}
.mrp-radio-tile:hover {
  border-color: var(--bop-color-border-strong);
  background: var(--bop-color-surface-muted);
}
.mrp-radio-tile--selected {
  border-color: var(--bop-color-primary);
  background: var(--bop-color-primary-light);
}
.mrp-radio-tile__title {
  font-weight: 600;
  font-size: var(--bop-font-sm);
  color: var(--bop-color-text-primary);
}
.mrp-radio-tile__help {
  font-size: var(--bop-font-xs);
  color: var(--bop-color-text-muted);
  margin-top: 2px;
}

.mrp-field-block {
  margin-top: var(--bop-spacing-3);
}
.mrp-info-chip {
  display: flex;
  align-items: center;
  gap: var(--bop-spacing-2);
  background: var(--bop-color-info-light);
  color: var(--bop-color-info);
  padding: var(--bop-spacing-2) var(--bop-spacing-3);
  border-radius: var(--bop-radius-sm);
  margin-top: var(--bop-spacing-2);
  font-size: var(--bop-font-xs);
}

.mrp-autocomplete {
  position: relative;
}
.mrp-autocomplete__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--bop-color-surface);
  border: 1px solid var(--bop-color-border-strong);
  border-radius: var(--bop-radius-md);
  box-shadow: var(--bop-shadow-md);
  z-index: 300;
  max-height: 280px;
  overflow-y: auto;
}
.mrp-autocomplete__item {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: var(--bop-spacing-2) var(--bop-spacing-3);
  cursor: pointer;
  border-bottom: 1px solid var(--bop-color-border);
}
.mrp-autocomplete__item:last-child { border-bottom: none; }
.mrp-autocomplete__item:hover { background: var(--bop-color-primary-light); }
.mrp-autocomplete__line1 {
  display: flex;
  gap: var(--bop-spacing-2);
  align-items: baseline;
}
.mrp-autocomplete__codice {
  font-family: var(--bop-font-mono, ui-monospace, monospace);
  font-size: var(--bop-font-xs);
  font-weight: 600;
  color: var(--bop-color-primary-dark);
}
.mrp-autocomplete__nome {
  font-size: var(--bop-font-sm);
  color: var(--bop-color-text-primary);
  font-weight: 500;
}
.mrp-autocomplete__line2 {
  display: flex;
  justify-content: space-between;
  gap: var(--bop-spacing-2);
  font-size: var(--bop-font-xs);
  color: var(--bop-color-text-muted);
  margin-top: 3px;
}
.mrp-autocomplete__meta { white-space: nowrap; }
.mrp-autocomplete__empty {
  padding: var(--bop-spacing-3);
  color: var(--bop-color-text-muted);
  text-align: center;
  font-size: var(--bop-font-sm);
}

.mrp-preconf-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--bop-spacing-3);
  padding: var(--bop-spacing-3);
  background: var(--bop-color-success-light);
  border: 1px solid var(--bop-color-success);
  border-radius: var(--bop-radius-md);
}
.mrp-preconf-selected__main {
  display: flex;
  align-items: center;
  gap: var(--bop-spacing-3);
  flex: 1;
  min-width: 0;
}
.mrp-preconf-selected__info { min-width: 0; }
.mrp-preconf-selected__nome {
  font-weight: 600;
  color: var(--bop-color-text-primary);
  font-size: var(--bop-font-sm);
}
.mrp-preconf-selected__meta {
  font-size: var(--bop-font-xs);
  color: var(--bop-color-text-secondary);
  margin-top: 2px;
}
.mrp-preconf-selected__actions {
  display: flex;
  gap: var(--bop-spacing-2);
  align-items: center;
}

.mrp-articolo-readout {
  background: var(--bop-color-surface-muted);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-sm);
  padding: var(--bop-spacing-2) var(--bop-spacing-3);
  margin-bottom: var(--bop-spacing-3);
}
.mrp-articolo-readout__label {
  font-size: var(--bop-font-xs);
  color: var(--bop-color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.mrp-articolo-readout__body {
  display: flex;
  align-items: center;
  gap: var(--bop-spacing-2);
}
.mrp-articolo-readout__desc {
  font-size: var(--bop-font-sm);
  color: var(--bop-color-text-primary);
  font-weight: 500;
}

.mrp-grid-4 {
  display: grid;
  grid-template-columns: 100px 1fr 1fr 1.5fr;
  gap: var(--bop-spacing-3);
  margin-top: var(--bop-spacing-3);
}

.mrp-empty {
  padding: var(--bop-spacing-4);
  background: var(--bop-color-surface-alt);
  border: 1px dashed var(--bop-color-border-strong);
  border-radius: var(--bop-radius-sm);
  text-align: center;
  color: var(--bop-color-text-muted);
  font-size: var(--bop-font-sm);
}

.mrp-badge-inline { margin-left: var(--bop-spacing-2); }

.mrp-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-sm);
  margin-bottom: var(--bop-spacing-3);
}
.mrp-comp-table { min-width: 1180px; font-size: var(--bop-font-xs); }
.mrp-comp-table th,
.mrp-comp-table td {
  padding: 6px 8px !important;
  vertical-align: middle !important;
}
.mrp-group-header-row th.mrp-group-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;
  font-weight: 600;
  padding: 4px 8px !important;
  border-bottom: 1px solid var(--bop-color-border-strong);
}
.mrp-group-header--struct { background: var(--bop-color-info-light); color: var(--bop-color-info); }
.mrp-group-header--qta    { background: var(--bop-color-warning-light); color: var(--bop-color-warning); }
.mrp-group-header--econ   { background: var(--bop-color-success-light); color: var(--bop-color-success); }
.mrp-group-header--actions { background: var(--bop-color-surface-alt); }

.mrp-inp-xs { width: 70px; padding: 4px 6px !important; }
.mrp-inp-sm { width: 95px; padding: 4px 6px !important; }

.mrp-qty-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mrp-calc-cell {
  font-weight: 600;
  background: var(--bop-color-success-light);
  color: var(--bop-color-success);
}

.mrp-tipologia-badge {
  font-size: 10px !important;
  letter-spacing: 0.3px;
}

.mrp-articolo-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: var(--bop-color-success-light);
  border: 1px solid var(--bop-color-success);
  border-radius: var(--bop-radius-sm);
  color: var(--bop-color-success);
  font-size: 11px;
  max-width: 260px;
}
.mrp-articolo-chip__desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--bop-color-text-primary);
  font-weight: normal;
}
.mrp-articolo-chip__x {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 2px;
  color: var(--bop-color-success);
  font-size: 12px;
  line-height: 1;
  opacity: 0.7;
}
.mrp-articolo-chip__x:hover { opacity: 1; }

.mrp-inter-hint {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  background: var(--bop-color-primary-light);
  border-radius: var(--bop-radius-sm);
  font-size: 10px;
  color: var(--bop-color-primary-dark);
}

.mrp-comp-actions {
  display: flex;
  gap: var(--bop-spacing-2);
}

.mrp-prezzi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--bop-spacing-3);
}
.mrp-prezzi-panel {
  padding: var(--bop-spacing-3);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-md);
  background: var(--bop-color-surface-muted);
}
.mrp-prezzi-panel--highlight {
  background: var(--bop-color-primary-light);
  border-color: var(--bop-color-primary);
}
.mrp-prezzi-panel__title {
  font-size: var(--bop-font-sm);
  font-weight: 600;
  margin-bottom: var(--bop-spacing-3);
  color: var(--bop-color-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.mrp-prezzo-calc-box {
  padding: var(--bop-spacing-3);
  background: var(--bop-color-surface);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-sm);
  margin-top: var(--bop-spacing-2);
}
.mrp-prezzo-calc-box__label {
  font-size: var(--bop-font-xs);
  color: var(--bop-color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.mrp-prezzo-calc-box__value {
  font-size: 20px;
  font-weight: 700;
  color: var(--bop-color-primary-dark);
  margin-top: 2px;
}

.mrp-check-label {
  display: flex;
  align-items: center;
  gap: var(--bop-spacing-2);
  padding: var(--bop-spacing-2) 0;
  cursor: pointer;
  font-size: var(--bop-font-sm);
}
.mrp-totali {
  margin-top: var(--bop-spacing-3);
  padding-top: var(--bop-spacing-3);
  border-top: 1px solid var(--bop-color-border);
}
.mrp-totali__row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: var(--bop-font-sm);
  color: var(--bop-color-text-secondary);
}
.mrp-totali__row--total {
  font-size: var(--bop-font-md);
  color: var(--bop-color-text-primary);
  padding-top: var(--bop-spacing-2);
  border-top: 1px solid var(--bop-color-border);
  margin-top: var(--bop-spacing-2);
}

.mrp-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.mrp-confirm-box {
  background: var(--bop-color-surface);
  border-radius: var(--bop-radius-md);
  padding: var(--bop-spacing-4);
  max-width: 480px;
  box-shadow: var(--bop-shadow-md);
}
.mrp-confirm-box__title {
  font-size: var(--bop-font-md);
  font-weight: 600;
  margin-bottom: var(--bop-spacing-2);
  color: var(--bop-color-danger);
}
.mrp-confirm-box__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--bop-spacing-2);
  margin-top: var(--bop-spacing-3);
}

.mrp-debug-panel {
  margin-top: var(--bop-spacing-4);
  padding: var(--bop-spacing-3);
  background: var(--bop-color-surface-muted);
  border: 1px solid var(--bop-color-border);
  border-radius: var(--bop-radius-sm);
  font-size: var(--bop-font-sm);
}
.mrp-debug-panel summary {
  cursor: pointer;
  font-weight: 600;
  color: var(--bop-color-primary-dark);
}
.mrp-debug-panel ul {
  margin-top: var(--bop-spacing-2);
  padding-left: var(--bop-spacing-4);
  color: var(--bop-color-text-secondary);
  line-height: 1.6;
}
.mrp-debug-panel code {
  background: var(--bop-color-surface);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}
`;
